import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, ArrowRight, CalendarCheck, Bot, TrendingUp, MessageSquare,
  Brain, Megaphone, BarChart3, Clock, Heart, Zap, ChevronDown, Star,
  Wand2, ShieldCheck,
} from "lucide-react";
import Reveal from "@/marketing/Reveal";
import PricingCards from "@/marketing/PricingCards";
import ComparisonTable from "@/marketing/ComparisonTable";
import { FAQS } from "@/marketing/data";
import { useI18n } from "@/i18n/I18nContext";

const HERO_IMG = "https://images.pexels.com/photos/13068377/pexels-photo-13068377.jpeg?auto=compress&cs=tinysrgb&w=1400";
const SALON_IMG = "https://images.unsplash.com/photo-1633681926035-ec1ac984418a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const FAQS_ES = [
  { q: "¿Qué es exactamente BellaOS?", a: "BellaOS es el sistema operativo con IA para negocios de belleza — reservas, CRM, pagos, inventario, marketing y reportes en una plataforma elegante, con agentes de IA que automatizan el trabajo tedioso." },
  { q: "¿BellaOS es solo para salones de cabello?", a: "No. BellaOS está hecho para salones de belleza, barberías, spas de lujo, estudios de uñas, estudios boutique y clínicas de bienestar. La plataforma se adapta a tus servicios, personal y precios." },
  { q: "¿Cómo ayuda la IA a mi negocio?", a: "La Recepcionista IA responde a tus clientes 24/7, el Agendador IA llena las horas tranquilas y el Generador de Marketing IA crea promociones automáticamente — ahorrando horas cada semana mientras aumenta tus ingresos." },
  { q: "¿Puedo cambiar de Basic a Elite después?", a: "Por supuesto. Puedes mejorar a Elite cuando quieras para desbloquear toda la suite de IA, personal ilimitado y multi-local — tus datos se mantienen al instante." },
  { q: "¿Necesito conocimientos técnicos?", a: "Para nada. BellaOS está diseñado para dueños de salón, no para ingenieros. Todo, desde reservas hasta marketing con IA, está a unos clics." },
  { q: "¿Mis datos están seguros?", a: "Sí. Cada negocio opera en un entorno aislado y encriptado, con respaldos en la nube y permisos por rol." },
];

function Section({ id, children, className = "" }) {
  return <section id={id} className={`py-24 sm:py-32 px-5 sm:px-8 ${className}`}>{children}</section>;
}
function Overline({ children }) {
  return <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#B86B5A] mb-4">{children}</p>;
}
function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#2C3E35]/10">
      <button data-testid={`faq-${idx}`} onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-6 text-left gap-4 group">
        <span className="font-display text-lg font-semibold text-[#0A0A0A] group-hover:text-[#B86B5A] transition-colors">{q}</span>
        <ChevronDown size={20} className={`shrink-0 text-[#2C3E35] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
        <p className="pb-6 text-[#2C3E35]/75 leading-relaxed max-w-2xl">{a}</p>
      </motion.div>
    </div>
  );
}

export default function Landing() {
  const { t, lang } = useI18n();
  const faqs = lang === "es" ? FAQS_ES : FAQS;

  const STATS = [
    { value: "+32%", label: t.stats.s1 }, { value: "−71%", label: t.stats.s2 },
    { value: "8 hrs", label: t.stats.s3 }, { value: "24/7", label: t.stats.s4 },
  ];
  const HOW = [
    { n: "01", title: t.how.s1t, desc: t.how.s1d, icon: Zap },
    { n: "02", title: t.how.s2t, desc: t.how.s2d, icon: Bot },
    { n: "03", title: t.how.s3t, desc: t.how.s3d, icon: TrendingUp },
  ];
  const AI_FEATURES = [
    { title: t.ai.f1t, desc: t.ai.f1d, icon: MessageSquare, span: "md:col-span-7" },
    { title: t.ai.f2t, desc: t.ai.f2d, icon: CalendarCheck, span: "md:col-span-5" },
    { title: t.ai.f3t, desc: t.ai.f3d, icon: Megaphone, span: "md:col-span-5" },
    { title: t.ai.f4t, desc: t.ai.f4d, icon: Brain, span: "md:col-span-7" },
  ];
  const testimonials = [
    { img: "https://images.pexels.com/photos/8834020/pexels-photo-8834020.jpeg?auto=compress&cs=tinysrgb&w=400", name: "Camille R.", role: lang === "es" ? "Dueña de Salón, San José" : "Salon Owner, San José", quote: lang === "es" ? "BellaOS es como tener un equipo de recepción completo. Mis inasistencias bajaron y por fin tengo mis fines de semana." : "BellaOS feels like having a full front-desk team. My no-shows dropped and my weekends are finally mine." },
    { img: "https://images.pexels.com/photos/8834025/pexels-photo-8834025.jpeg?auto=compress&cs=tinysrgb&w=400", name: "Sofía P.", role: lang === "es" ? "Especialista en Color" : "Color Specialist", quote: lang === "es" ? "Las promociones con IA llenaron mis días más tranquilos. Recuperé la suscripción en la primera semana." : "The AI promotions filled my quietest days. I made back the subscription in the first week." },
    { img: "https://images.unsplash.com/photo-1559069309-020e226c60f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", name: "Marina L.", role: lang === "es" ? "Directora de Spa" : "Spa Director", quote: lang === "es" ? "Maneja las operaciones para que yo me enfoque en la experiencia. Es la herramienta más premium que hemos usado." : "It runs the operations so I can focus on the experience. It's the most premium tool we've used." },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative pt-36 sm:pt-44 pb-24 px-5 sm:px-8 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-[-10%] right-[-5%] h-[500px] w-[500px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
          <div className="absolute bottom-[-20%] left-[-10%] h-[500px] w-[500px] rounded-full bg-[#2C3E35]/8 blur-[120px]" />
        </div>
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[#2C3E35]/10 shadow-sm mb-8">
            <Sparkles size={15} className="text-[#D4AF37]" />
            <span className="text-sm font-medium text-[#2C3E35]">{t.hero.badge}</span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tighter leading-[0.95] max-w-4xl text-[#0A0A0A]">
            {t.hero.title1}{" "}
            <span className="font-serif-lux italic font-medium text-[#B86B5A]">{t.hero.title2}</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-7 text-lg sm:text-xl text-[#2C3E35]/75 max-w-2xl leading-relaxed">{t.hero.subtitle}</motion.p>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <Link to="/register" data-testid="hero-cta-trial"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#2C3E35] text-[#FDFBF9] font-semibold hover:bg-[#0A0A0A] transition-all duration-300 hover:shadow-[0_0_40px_rgba(212,175,55,0.3)]">
              {t.hero.ctaTrial} <ArrowRight size={18} />
            </Link>
            <Link to="/pricing" data-testid="hero-cta-demo"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white border border-[#2C3E35]/15 text-[#0A0A0A] font-semibold hover:border-[#2C3E35]/40 transition-colors">
              {t.hero.ctaPricing}
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-16 relative rounded-3xl overflow-hidden border border-[#2C3E35]/10 shadow-[0_30px_80px_rgba(44,62,53,0.15)]">
            <img src={HERO_IMG} alt="Luxury salon interior" className="w-full h-[280px] sm:h-[460px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2C3E35]/40 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 sm:bottom-8 sm:left-8 flex flex-wrap gap-3">
              {[{ icon: Bot, t: t.hero.card1 }, { icon: CalendarCheck, t: t.hero.card2 }, { icon: TrendingUp, t: t.hero.card3 }].map((c, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2.5 rounded-full backdrop-blur-xl bg-[#FDFBF9]/85 border border-white/40 text-sm font-medium text-[#0A0A0A] shadow-lg">
                  <c.icon size={16} className="text-[#B86B5A]" /> {c.t}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="px-5 sm:px-8 pb-8">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="text-center sm:text-left">
              <div className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#2C3E35]">{s.value}</div>
              <div className="text-sm text-[#2C3E35]/65 mt-1">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY */}
      <Section id="why">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <Overline>{t.why.overline}</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-[#0A0A0A]">
              {t.why.title1} <span className="font-serif-lux italic font-medium">{t.why.title2}</span>
            </h2>
            <p className="mt-6 text-lg text-[#2C3E35]/75 leading-relaxed">{t.why.body}</p>
            <div className="mt-8 space-y-5">
              {[{ icon: Clock, tt: t.why.f1t, dd: t.why.f1d }, { icon: Heart, tt: t.why.f2t, dd: t.why.f2d }, { icon: ShieldCheck, tt: t.why.f3t, dd: t.why.f3d }].map((f, i) => (
                <div key={i} className="flex gap-4">
                  <div className="h-11 w-11 rounded-xl bg-[#2C3E35]/6 flex items-center justify-center shrink-0">
                    <f.icon size={20} strokeWidth={1.5} className="text-[#2C3E35]" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#0A0A0A]">{f.tt}</div>
                    <div className="text-sm text-[#2C3E35]/70">{f.dd}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative rounded-3xl overflow-hidden border border-[#2C3E35]/10 shadow-[0_20px_60px_rgba(44,62,53,0.12)]">
              <img src={SALON_IMG} alt="Elegant salon" className="w-full h-[480px] object-cover" />
            </div>
          </Reveal>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="how" className="bg-[#F5F2ED]">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <Overline>{t.how.overline}</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A]">{t.how.title}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {HOW.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12}>
                <div className="h-full rounded-2xl bg-white border border-[#2C3E35]/10 p-8 shadow-[0_8px_32px_rgba(44,62,53,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_48px_rgba(44,62,53,0.12)]">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-display text-5xl font-extrabold text-[#2C3E35]/12">{s.n}</span>
                    <div className="h-12 w-12 rounded-xl bg-[#2C3E35] flex items-center justify-center">
                      <s.icon size={22} strokeWidth={1.5} className="text-[#D4AF37]" />
                    </div>
                  </div>
                  <h3 className="font-display text-xl font-bold text-[#0A0A0A]">{s.title}</h3>
                  <p className="mt-2 text-[#2C3E35]/70 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* AI FEATURES BENTO */}
      <Section id="ai">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-14">
            <Overline>{t.ai.overline}</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A]">
              {t.ai.title1} <span className="font-serif-lux italic font-medium text-[#B86B5A]">{t.ai.title2}</span>
            </h2>
            <p className="mt-5 text-lg text-[#2C3E35]/75 leading-relaxed">{t.ai.body}</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {AI_FEATURES.map((f, i) => (
              <Reveal key={i} delay={i * 0.08} className={f.span}>
                <div className="h-full rounded-2xl bg-[#2C3E35] text-[#FDFBF9] p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_50px_rgba(212,175,55,0.15)] relative overflow-hidden">
                  <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-[#D4AF37]/10 blur-2xl" />
                  <div className="h-12 w-12 rounded-xl bg-[#FDFBF9]/10 flex items-center justify-center mb-5">
                    <f.icon size={22} strokeWidth={1.5} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="font-display text-2xl font-bold">{f.title}</h3>
                  <p className="mt-2 text-[#FDFBF9]/70 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* MARKETING + BI */}
      <Section className="bg-[#F5F2ED]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="h-full rounded-3xl bg-white border border-[#2C3E35]/10 p-10 shadow-[0_8px_32px_rgba(44,62,53,0.06)]">
              <Megaphone size={28} strokeWidth={1.5} className="text-[#B86B5A] mb-5" />
              <h3 className="font-display text-3xl font-bold text-[#0A0A0A]">{t.marketing.mTitle}</h3>
              <p className="mt-3 text-[#2C3E35]/75 leading-relaxed">{t.marketing.mBody}</p>
              <Link to="/promotions" data-testid="marketing-promotions-link" className="mt-6 inline-flex items-center gap-2 font-semibold text-[#2C3E35] hover:text-[#B86B5A] transition-colors">
                {t.marketing.mLink} <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full rounded-3xl bg-white border border-[#2C3E35]/10 p-10 shadow-[0_8px_32px_rgba(44,62,53,0.06)]">
              <BarChart3 size={28} strokeWidth={1.5} className="text-[#B86B5A] mb-5" />
              <h3 className="font-display text-3xl font-bold text-[#0A0A0A]">{t.marketing.biTitle}</h3>
              <p className="mt-3 text-[#2C3E35]/75 leading-relaxed">{t.marketing.biBody}</p>
              <div className="mt-6 grid grid-cols-3 gap-3">
                {t.marketing.biTags.map((tag) => (
                  <div key={tag} className="rounded-xl bg-[#F5F2ED] p-3 text-center">
                    <div className="text-xs text-[#2C3E35]/60">{tag}</div>
                    <TrendingUp size={18} className="text-[#2C3E35] mx-auto mt-1.5" />
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* TESTIMONIALS */}
      <Section>
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-14">
            <Overline>{t.testimonials.overline}</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A]">{t.testimonials.title}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((tm, i) => (
              <Reveal key={tm.name} delay={i * 0.1}>
                <div className="h-full rounded-2xl bg-white border border-[#2C3E35]/10 p-7 shadow-[0_8px_32px_rgba(44,62,53,0.06)]">
                  <div className="flex gap-1 mb-4">{[...Array(5)].map((_, k) => <Star key={k} size={15} fill="#D4AF37" className="text-[#D4AF37]" />)}</div>
                  <p className="text-[#2C3E35]/85 leading-relaxed">"{tm.quote}"</p>
                  <div className="mt-6 flex items-center gap-3">
                    <img src={tm.img} alt={tm.name} className="h-11 w-11 rounded-full object-cover" />
                    <div>
                      <div className="font-semibold text-[#0A0A0A] text-sm">{tm.name}</div>
                      <div className="text-xs text-[#2C3E35]/60">{tm.role}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* PRICING */}
      <Section id="pricing" className="bg-[#F5F2ED]">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <Overline>{t.pricing.overline}</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A]">{t.pricing.title}</h2>
            <p className="mt-4 text-lg text-[#2C3E35]/75">{t.pricing.subtitle}</p>
          </Reveal>
          <Reveal delay={0.1}><PricingCards /></Reveal>
          <Reveal delay={0.15} className="mt-16">
            <h3 className="font-display text-2xl font-bold text-center text-[#0A0A0A] mb-8">{t.pricing.compare}</h3>
            <div className="max-w-4xl mx-auto"><ComparisonTable /></div>
          </Reveal>
        </div>
      </Section>

      {/* FAQ */}
      <Section>
        <div className="max-w-3xl mx-auto">
          <Reveal className="text-center mb-12">
            <Overline>{t.faq.overline}</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A]">{t.faq.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            {faqs.map((f, i) => <FAQItem key={i} {...f} idx={i} />)}
          </Reveal>
        </div>
      </Section>

      {/* FINAL CTA */}
      <section className="px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="max-w-6xl mx-auto rounded-[2rem] bg-[#2C3E35] text-[#FDFBF9] p-12 sm:p-20 text-center relative overflow-hidden">
            <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-[#D4AF37]/15 blur-[100px]" />
            <div className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-[#B86B5A]/15 blur-[100px]" />
            <div className="relative">
              <Wand2 size={32} className="text-[#D4AF37] mx-auto mb-6" />
              <h2 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight leading-tight max-w-3xl mx-auto">
                {t.cta.title1} <span className="font-serif-lux italic font-medium text-[#D4AF37]">{t.cta.title2}</span>
              </h2>
              <p className="mt-6 text-lg text-[#FDFBF9]/75 max-w-xl mx-auto">{t.cta.body}</p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                <Link to="/register" data-testid="footer-cta-trial"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D4AF37] text-[#2C3E35] font-bold hover:bg-[#e0bd4a] transition-colors">
                  {t.cta.trial} <ArrowRight size={18} />
                </Link>
                <Link to="/pricing" className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-[#FDFBF9]/25 text-[#FDFBF9] font-semibold hover:bg-[#FDFBF9]/10 transition-colors">
                  {t.cta.demo}
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
